<script setup lang="ts">
import { ref } from 'vue';

type Currency = {
  code: string;
  symbol: string;
  name: string;
  color: string;
};

const currencies: Currency[] = [
  { code: 'EUR', symbol: '€', name: 'Euro', color: '#003399' },
  { code: 'USD', symbol: '$', name: 'US Dollar', color: '#6CDE07' },
  { code: 'GBP', symbol: '£', name: 'British Pound', color: '#BC3FE0' }
];

const selectedCurrency = ref<Currency>(currencies[0]);
const isOpen = ref(false);

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const selectCurrency = (currency: Currency) => {
  selectedCurrency.value = currency;
  isOpen.value = false;
};
</script>

<template>
  <div class="relative w-auto">
    <button
      @click="toggleDropdown"
      class="flex items-center gap-3 px-6 py-3.5 text-base border bg-gray-800/30 border-gray-700/50 text-white hover:bg-gray-700/40 rounded-2xl transition-all duration-200"
    >
      <div
        class="w-7 h-7 rounded-full flex justify-center items-center text-white font-bold text-sm bg-blue-600"
      >
        {{ selectedCurrency.symbol }}
      </div>
      <div class="text-base font-medium">
        {{ selectedCurrency.code }}
      </div>
      <svg
        class="w-5 h-5 transition-transform"
        :class="{ 'rotate-180': isOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        stroke-width="2"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"></path>
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen"
      class="absolute top-full mt-2 right-0 w-64 bg-gray-800 rounded-2xl shadow-xl border border-gray-700/50 z-50 overflow-hidden"
    >
      <button
        v-for="currency in currencies"
        :key="currency.code"
        @click="selectCurrency(currency)"
        class="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-700/50 transition-colors text-white"
        :class="{ 'bg-gray-700/30': selectedCurrency.code === currency.code }"
      >
        <div
          class="w-8 h-8 rounded-full flex justify-center items-center text-white font-bold text-sm"
          :style="{ backgroundColor: currency.color }"
        >
          {{ currency.symbol }}
        </div>
        <div class="flex flex-col items-start">
          <div class="text-white font-medium">{{ currency.code }}</div>
          <div class="text-gray-400 text-sm">{{ currency.name }}</div>
        </div>
      </button>
    </div>
  </div>
</template>

<style scoped>
.rotate-180 {
  transform: rotate(180deg);
}
</style>
